import { pick } from 'lodash';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import { denormalisedResponseEntities } from '../../util/data';

// ================ Action types ================ //

const ActionTypes = {
  SET_INITIAL_VALUES: 'app/ServiceOfferingsPage/SET_INITIAL_VALUES',

  FETCH_LISTING_REQUEST: 'app/ServiceOfferingsPage/FETCH_LISTING_REQUEST',
  FETCH_LISTING_SUCCESS: 'app/ServiceOfferingsPage/FETCH_LISTING_SUCCESS',
  FETCH_LISTING_ERROR: 'app/ServiceOfferingsPage/FETCH_LISTING_ERROR',

  FETCH_TIME_SLOTS_REQUEST: 'app/ServiceOfferingsPage/FETCH_TIME_SLOTS_REQUEST',
  FETCH_TIME_SLOTS_SUCCESS: 'app/ServiceOfferingsPage/FETCH_TIME_SLOTS_SUCCESS',
  FETCH_TIME_SLOTS_ERROR: 'app/ServiceOfferingsPage/FETCH_TIME_SLOTS_ERROR',
};

// ================ Initial State ================ //

const initialState = {
  fetchListingInProgress: false,
  fetchListingError: null,
  monthlyTimeSlots: {
    // '2025-11': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
};

// ================ Reducers ================ //

const ServiceOfferingsPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ActionTypes.SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case ActionTypes.FETCH_LISTING_REQUEST:
      return { ...state, fetchListingInProgress: true, fetchListingError: null };
    case ActionTypes.FETCH_LISTING_SUCCESS:
      return {
        ...state,
        fetchListingInProgress: false,
        fetchListingError: null,
      };
    case ActionTypes.FETCH_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };

    case ActionTypes.FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case ActionTypes.FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case ActionTypes.FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    default:
      return state;
  }
};

// ================ Selectors ================ //

export const fetchListingInProgressSelector = state =>
  state.ServiceOfferingsPage.fetchListingInProgress;
export const monthlyTimeSlotsSelector = state => state.ServiceOfferingsPage.monthlyTimeSlots;

// ================ Actions Creators ================ //
export const setInitialValues = initialValues => ({
  type: ActionTypes.SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchListingRequest = () => ({ type: ActionTypes.FETCH_LISTING_REQUEST });
const fetchListingSuccess = () => ({ type: ActionTypes.FETCH_LISTING_SUCCESS });
const fetchListingError = error => ({
  type: ActionTypes.FETCH_LISTING_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: ActionTypes.FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: ActionTypes.FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: ActionTypes.FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

// ================ Thunks ================ //

export const fetchListing = (id, config) => async (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  dispatch(fetchListingRequest());
  try {
    const params = {
      id,
      include: ['author', 'author.profileImage', 'author.publicData', 'images', 'currentStock'],
      'fields.image': [
        'variants.default',
        // Scaled variants for large images
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',

        // Cropped variants for listing thumbnail images
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,

        // Social media
        'variants.facebook',
        'variants.twitter',

        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
    };
    const response = await sdk.listings.show(params);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(fetchListingSuccess());
    return response;
  } catch (e) {
    dispatch(fetchListingError(storableError(e)));
  }
};

export const timeSlotsRequest = params => async (dispatch, getState, sdk) => {
  const response = await sdk.timeslots.query(params);
  return denormalisedResponseEntities(response);
};
export const fetchTimeSlots = (listingId, start, end, timeZone) => async (
  dispatch,
  getState,
  sdk
) => {
  const monthId = monthIdString(start, timeZone);
  dispatch(fetchTimeSlotsRequest(monthId));

  try {
    // The maximum pagination page size for timeSlots is 500
    const params = {
      listingId,
      start,
      end,
      perPage: 500,
      page: 1,
    };

    const timeSlots = await dispatch(timeSlotsRequest(params));
    dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
  } catch (error) {
    dispatch(fetchTimeSlotsError(monthId, storableError(error)));
  }
};

const fetchMonthlyTimeSlots = async (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  if (!hasWindow) return;

  const { attributes } = listing || {};
  const availabilityPlan = attributes?.availabilityPlan;
  const timezone = availabilityPlan?.timezone;
  if (!listing?.id || !timezone) {
    return; // Nothing to fetch
  }

  const unitType = attributes?.publicData?.unitType || 'day';
  const timeUnit = unitType === 'hour' ? 'hour' : 'day';
  const now = new Date();
  const nextBoundary = findNextBoundary(now, timeUnit, timezone);

  const nextMonth = getStartOf(nextBoundary, 'month', timezone, 1, 'months');
  const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');

  try {
    await Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, timezone)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, timezone)),
    ]);
  } catch (error) {
    // Optional: log or dispatch a global error if needed
    console.warn('Failed to fetch monthly time slots:', error);
    // Don't throw — we want the page to load even if calendar fails
  }
};
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { id } = params || {};
  if (id) {
    const response = await dispatch(fetchListing(id, config));
    const listing = response?.data?.data;
    fetchMonthlyTimeSlots(dispatch, listing);
  }
  const currentUser = await dispatch(fetchCurrentUser({ enforce: true }));
  const currentUserId = currentUser?.id;

  return currentUserId;
};

export default ServiceOfferingsPageReducer;
